import { TreeInteractor } from '../tree-domain/TreeInteractor';
import { validateTreeName as validateTreeNameValue } from '../tree-domain/treeNameValidation';

export interface TitleInteractorViewState {
    treeName: string;
    treeNameError: string | null;
    isEditing: boolean;
    showEditButton: boolean;
    showSaveButton: boolean;
    showCancelButton: boolean;
    isSaving: boolean;
    error: string | null;
}

export class TitleInteractor {
    private treeName: string = '';
    private originalTreeName: string = '';
    private isEditing: boolean = false;
    private isSaving: boolean = false;
    private error: string | null = null;
    private treeNameError: string | null = null;
    private isEditMode: boolean = false;
    private isLoading: boolean = true;

    constructor(private readonly treeInteractor: TreeInteractor) {
        // Initialize with current tree name from TreeInteractor
        const currentData = this.treeInteractor.getData();
        this.treeName = currentData.name;
        this.originalTreeName = currentData.name;
    }

    setEditMode(isEditMode: boolean): void {
        this.isEditMode = isEditMode;
    }

    setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }

    setTreeName(name: string): void {
        this.treeName = name;
        this.error = null;
        this.treeNameError = null;
    }

    startEditing(): void {
        // Store current name as original
        const currentData = this.treeInteractor.getData();
        this.originalTreeName = currentData.name;
        this.treeName = currentData.name;
        this.isEditing = true;
        this.error = null;
        this.treeNameError = null;
    }

    cancelEditing(): void {
        this.treeName = this.originalTreeName;
        this.isEditing = false;
        this.error = null;
        this.treeNameError = null;
    }

    async saveTreeName(): Promise<void> {
        // Check if data is loaded before allowing save
        if (this.isLoading) {
            throw new Error('Cannot save: data is still loading');
        }
        
        // Validate before attempting to save
        const treeNameError = this.validateTreeName();

        if (treeNameError) {
            // Errors are already set in ViewState, just throw to indicate failure
            throw new Error('Validation failed');
        }

        this.isSaving = true;
        this.error = null;
        this.treeNameError = null;

        try {
            const trimmedName = this.treeName.trim();
            this.treeInteractor.updateTreeName(trimmedName);
            // Update both treeName and originalTreeName after successful save
            // This ensures the saved name is preserved
            this.treeName = trimmedName;
            this.originalTreeName = trimmedName;
            this.isEditing = false;
        } catch (error) {
            this.isSaving = false;
            
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.error = errorMessage;
            throw error;
        } finally {
            this.isSaving = false;
        }
    }

    getViewState(): TitleInteractorViewState {
        // Update tree name from TreeInteractor if not editing (in case it changed externally)
        // But only if the tree name actually changed to avoid unnecessary updates
        if (!this.isEditing) {
            const currentData = this.treeInteractor.getData();
            const currentName = currentData.name;
            // Only sync if the name actually changed (and it's not the same as what we have)
            if (currentName !== this.treeName) {
                this.treeName = currentName;
                this.originalTreeName = currentName;
            }
        }

        // Use stored errors if set, otherwise validate
        const treeNameError = this.treeNameError ?? this.validateTreeName();

        // Show edit button when not editing and in edit mode (but not while loading)
        const showEditButton = !this.isEditing && this.isEditMode && !this.isLoading;
        
        // Show save button when editing and valid (not saving)
        const showSaveButton = this.isEditing && !treeNameError && !this.isSaving;
        
        // Show cancel button when editing
        const showCancelButton = this.isEditing;

        return {
            treeName: this.treeName,
            treeNameError,
            isEditing: this.isEditing,
            showEditButton,
            showSaveButton,
            showCancelButton,
            isSaving: this.isSaving,
            error: this.error
        };
    }

    private validateTreeName(): string | null {
        return validateTreeNameValue(this.treeName);
    }
}

